import React, { useState } from "react";
import { toast } from "sonner";
import { http } from "@/lib/api";
import { PageHeader, Section, Reveal, Eyebrow } from "@/components/Section";
import { TID } from "@/constants/testIds";

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-[10px] uppercase tracking-[0.35em] text-white/50">{label}</span>
    <div className="mt-2">{children}</div>
  </label>
);

const baseInput =
  "w-full bg-black/60 border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-white/30 focus:outline-none focus:border-[#FF9933] focus:ring-1 focus:ring-[#FF9933]/60 transition-all";

const Licensing = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    subject: "Music Licensing Enquiry",
    message: "",
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await http.post("/contact", { ...form, kind: "licensing" });
      toast.success("Received. Our team will respond within 48 hours.");
      setForm({ name: "", email: "", subject: "Music Licensing Enquiry", message: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <PageHeader
        eyebrow="Music Licensing"
        hindi="संगीत लाइसेंस"
        title="License KASHiX for Film, Brand &amp; Sync"
        subtitle="Available for film scoring, series, ad campaigns, yoga & wellness brands, streaming trailers and cinematic sync placements."
      />
      <Section className="!py-16">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Reveal>
              <Eyebrow>What we license</Eyebrow>
              <ul className="mt-6 space-y-4 text-white/70">
                {[
                  ["Film & Series", "Devotional cues, chase scores, meditation moments, spiritual awakening sequences."],
                  ["Brand & Advertising", "Wellness, yoga, luxury heritage, spiritual tourism and Indian heritage campaigns."],
                  ["Streaming & Trailers", "Trailer music with cinematic Sanskrit vocals."],
                  ["Yoga & Meditation Apps", "Track packs and custom cues for premium wellness platforms."],
                  ["Live & Immersive", "Cinematic devotional experiences, temple visualisations, immersive audio installations."],
                ].map(([h, s]) => (
                  <li key={h} className="flex gap-4">
                    <span className="mt-2 h-1.5 w-1.5 rounded-full bg-[#FF9933]" />
                    <div>
                      <div className="text-white">{h}</div>
                      <div className="text-sm text-white/60">{s}</div>
                    </div>
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <Reveal delay={0.1}>
              <form onSubmit={submit} className="glass rounded-2xl p-8 space-y-5">
                <div className="grid md:grid-cols-2 gap-5">
                  <Field label="Your Name">
                    <input data-testid={TID.licensingName} required className={baseInput} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full name" />
                  </Field>
                  <Field label="Email">
                    <input data-testid={TID.licensingEmail} required type="email" className={baseInput} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@company.com" />
                  </Field>
                </div>
                <Field label="Project / Subject">
                  <input data-testid={TID.licensingSubject} required className={baseInput} value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="Project name & sync intent" />
                </Field>
                <Field label="Tell us about the placement">
                  <textarea data-testid={TID.licensingMessage} required rows={6} className={`${baseInput} resize-none`} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Media type, territory, duration, budget range, timelines..." />
                </Field>
                <button
                  data-testid={TID.licensingSubmit}
                  disabled={busy}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#FF9933] text-black px-6 py-3 text-xs uppercase tracking-[0.3em] font-semibold hover:bg-[#FFD700] hover:shadow-[0_0_30px_rgba(255,153,51,0.55)] transition-all disabled:opacity-60"
                >
                  {busy ? "Sending..." : "Request Licensing"}
                </button>
              </form>
            </Reveal>
          </div>
        </div>
      </Section>
    </>
  );
};

export default Licensing;
